/**
 * Filters for pairs.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { SegmentedControl } from '@/components/forms/SegmentedControl';
import { SearchInput } from '@/components/forms/SearchInput';
import { Select } from '@/components/forms/Select';
import { MultiSelect } from '@/components/forms/MultiSelect';
import { DateRangeInput } from '@/components/forms/DateRangeInput';
import { useAuthStore } from '@/stores/authStore';
import { useProgramsForMode } from '@/hooks/useProgramsForMode';
import { getSchoolYears } from '@/services/school-years.service';
import { getEstablishments } from '@/services/establishments.service';
import {
  DATE_RANGE_PERIOD_OPTIONS_FULL,
  PAIR_STATUS_CONFIG,
} from '@/components/pairs/pair-configs';
import type { PairFilters, PairFilterGroupId, PairStatus } from '@/types/remote/pair.types';
import { ManagedByMultiSelect } from '@/components/common/ManagedByMultiSelect';
import {
  formatPairFilterDateToString,
  mapProgramFilterOptions,
  toggleListValue,
} from './pair-table-utils';

/** Props for {@link PairFiltersContent}. */
interface Props {
  filters: PairFilters;
  visibleFilterIds: PairFilterGroupId[];
  onFilterChange: (patch: Partial<PairFilters>) => void;
}

/** Remote pair filter ribbon; visibility comes from BD interface elements. */
export const PairFiltersContent: React.FC<Props> = ({
  filters,
  visibleFilterIds,
  onFilterChange,
}) => {
  const show = (id: PairFilterGroupId) => visibleFilterIds.includes(id);
  const { roles } = useAuthStore();
  const isCoordinator = roles?.[0]?.code === 'coordinator';

  return (
    <div className="filter-groups-container">
      {show('filter-status') && (
        <div className="filter-group">
          <span className="filter-group-label">Statut</span>
          <div className="filter-options">
            {(
              Object.entries(PAIR_STATUS_CONFIG) as [
                PairStatus,
                (typeof PAIR_STATUS_CONFIG)[PairStatus],
              ][]
            ).map(([key, cfg]) => {
              const isActive = filters.statuses.includes(key);
              const IconComponent = cfg.icon;
              return (
                <Button
                  key={key}
                  variant="custom"
                  size="sm"
                  className={cn(
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                    isActive ? 'filter-badge-active' : 'filter-badge-inactive',
                  )}
                  style={
                    { '--filter-color': cfg.badgeColor ?? cfg.lineColor } as React.CSSProperties
                  }
                  onClick={() => {
                    onFilterChange({ statuses: toggleListValue(filters.statuses, key) });
                  }}
                >
                  <IconComponent className="w-3.5 h-3.5" />
                  {cfg.label}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {show('filter-search') && (
        <div className="filter-group">
          <span className="filter-group-label">Recherche</span>
          <div className="w-64">
            <SearchInput
              size="sm"
              shortcut={false}
              placeholder="Rechercher par tuteur, élève..."
              value={filters.search ?? ''}
              onChange={(e) => onFilterChange({ search: e.target.value || undefined })}
              onClear={() => onFilterChange({ search: undefined })}
            />
          </div>
        </div>
      )}

      {show('filter-period') && (
        <div className="filter-group">
          <span className="filter-group-label">Période</span>
          <SegmentedControl
            size="sm"
            value={filters.customDateFrom ? '' : (filters.dateRange ?? 'all')}
            onChange={(v) => {
              onFilterChange({
                dateRange: v as PairFilters['dateRange'],
                customDateFrom: undefined,
                customDateTo: undefined,
              });
            }}
            options={[...DATE_RANGE_PERIOD_OPTIONS_FULL]}
          />
        </div>
      )}

      {show('filter-date-range') && (
        <div className="filter-group">
          <span className="filter-group-label">Plage de dates</span>
          <div className="w-auto">
            <DateRangeInput
              layout="horizontal"
              showConnector={true}
              compact={true}
              hidePickerLabels={true}
              showPresets={false}
              startDate={formatPairFilterDateToString(filters.customDateFrom)}
              endDate={formatPairFilterDateToString(filters.customDateTo)}
              onStartDateChange={(val) =>
                onFilterChange({ customDateFrom: val ? new Date(val) : undefined })
              }
              onEndDateChange={(val) =>
                onFilterChange({ customDateTo: val ? new Date(val) : undefined })
              }
            />
          </div>
        </div>
      )}

      {show('filter-managed-by') && (
        <div className="filter-group">
          <span className="filter-group-label">Géré par</span>
          <div className="w-64">
            <ManagedByMultiSelect
              value={filters.managedByIds}
              onChange={(val) => onFilterChange({ managedByIds: val })}
              inputSize="sm"
            />
          </div>
        </div>
      )}

      {show('filter-establishment') && (
        <EstablishmentFilter filters={filters} onFilterChange={onFilterChange} />
      )}

      {show('filter-program') && !isCoordinator && (
        <ProgramFilter filters={filters} onFilterChange={onFilterChange} />
      )}

      {show('filter-school-year') && (
        <SchoolYearFilter filters={filters} onFilterChange={onFilterChange} />
      )}
    </div>
  );
};

/** Props for {@link ProgramFilter} / {@link SchoolYearFilter} / {@link EstablishmentFilter}. */
interface SubFilterProps {
  filters: PairFilters;
  onFilterChange: (patch: Partial<PairFilters>) => void;
}

/** Program {@link MultiSelect} — values are UUIDs; backend resolves to the stored pair program. */
const ProgramFilter: React.FC<SubFilterProps> = ({ filters, onFilterChange }) => {
  const { data: programsData } = useProgramsForMode();

  const options = React.useMemo(() => mapProgramFilterOptions(programsData?.data), [programsData]);

  return (
    <div className="filter-group">
      <span className="filter-group-label">Programme</span>
      <div className="w-64">
        <MultiSelect
          placeholder="Tous les programmes"
          inputSize="sm"
          options={options}
          value={filters.programs}
          onChange={(vals) => onFilterChange({ programs: vals })}
          showSelectAll={false}
          maxVisibleTags={2}
        />
      </div>
    </div>
  );
};

/** School-year multi-select used in remote pair filters. */
const SchoolYearFilter: React.FC<SubFilterProps> = ({ filters, onFilterChange }) => {
  const { data: schoolYearsData } = useQuery({
    queryKey: ['school-years', 'filter-pairs'],
    queryFn: () => getSchoolYears({ pageSize: 20 }),
  });

  const options = React.useMemo(
    () =>
      schoolYearsData?.data.map((sy) => ({
        value: sy.id,
        label: sy.label ?? sy.code ?? sy.id,
      })) ?? [],
    [schoolYearsData],
  );

  return (
    <div className="filter-group">
      <span className="filter-group-label">Année scolaire</span>
      <div className="w-48">
        <Select
          key={filters.schoolYearId ?? '__empty__'}
          placeholder="Toutes les années"
          size="sm"
          value={filters.schoolYearId ?? ''}
          onChange={(v) => onFilterChange({ schoolYearId: v || undefined })}
          options={options}
        />
      </div>
    </div>
  );
};

/** Establishment multi-select used in remote pair filters. */
const EstablishmentFilter: React.FC<SubFilterProps> = ({ filters, onFilterChange }) => {
  const { data: establishmentsData } = useQuery({
    queryKey: ['pair-filter-establishments'],
    queryFn: () => getEstablishments({ pageSize: 10 }),
  });

  const options = React.useMemo(
    () =>
      (establishmentsData?.data ?? []).map((e) => ({
        value: e.id,
        label: e.name,
      })),
    [establishmentsData],
  );

  return (
    <div className="filter-group">
      <span className="filter-group-label">Établissement</span>
      <div className="w-48">
        <Select
          key={filters.establishmentId ?? '__empty__'}
          size="sm"
          value={filters.establishmentId ?? ''}
          onChange={(val) => onFilterChange({ establishmentId: val || undefined })}
          options={[{ value: '', label: 'Tous' }, ...options]}
        />
      </div>
    </div>
  );
};
